/**
 * Copyright (c) Simam Digital.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { getAuth, onAuthStateChanged, signInAnonymously } from 'firebase/auth';

import { firebaseConfig } from '../../config/firebaseConfig';
import { getFirestore } from 'firebase/firestore';
import { initializeApp } from 'firebase/app';

let app = null;
let auth = null;
let db = null;

/**
 * Whether real Firebase credentials have been filled into firebaseConfig.
 *
 * The shared-world layer (auth + Firestore plots) only activates when this is
 * true. While the config still holds the `REPLACE_ME` placeholders, the whole
 * layer no-ops and the game runs as a single-player local-save experience, so
 * the app is never broken by missing credentials. Fill the values in
 * `src/js/config/firebaseConfig.js` to turn the shared world on.
 * @returns {boolean}
 */
export function isFirebaseConfigured() {
	return (
		!!firebaseConfig.apiKey &&
		firebaseConfig.apiKey !== 'REPLACE_ME' &&
		!!firebaseConfig.appId &&
		firebaseConfig.appId !== 'REPLACE_ME'
	);
}

/**
 * Initialize the Firebase app (idempotent).
 * @returns {{ app: import('firebase/app').FirebaseApp, auth: any, db: any } | null}
 *   null when Firebase is not configured.
 */
export function initFirebase() {
	if (!isFirebaseConfigured()) {
		return null;
	}
	if (!app) {
		app = initializeApp(firebaseConfig);
		auth = getAuth(app);
		db = getFirestore(app);
	}
	return { app, auth, db };
}

/** @returns the Firestore instance, or null when Firebase is not configured. */
export function getDb() {
	return initFirebase()?.db ?? null;
}

/**
 * Ensure there is a signed-in user, signing in anonymously if needed.
 * The Firebase SDK persists the anonymous identity in IndexedDB, so the same
 * uid (and therefore the same plot) is reused across visits on this device.
 * @returns {Promise<import('firebase/auth').User|null>} null when unconfigured.
 */
export function ensureSignedIn() {
	const initialized = initFirebase();
	if (!initialized) {
		return Promise.resolve(null);
	}
	const { auth } = initialized;
	return new Promise((resolve, reject) => {
		const unsubscribe = onAuthStateChanged(auth, (user) => {
			if (user) {
				unsubscribe();
				resolve(user);
			}
		});
		if (!auth.currentUser) {
			signInAnonymously(auth).catch(reject);
		}
	});
}
